import { FlattenType, PathValue } from '@jms/model/types';
import { DateTime } from 'luxon';
import { Entity } from '../../entity';
import { FiltersNumberMethod } from '../../../utilities/filters.number.utility';
import { FiltersRelativeDateMethod } from '../../../utilities/filters.relative.date.utility';
import { MetadataEntityFieldData } from '../../../metadata/entity/decorators/model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../../../metadata/entity/decorators/model/metadata.entity.field.types';
import { getMetadataEntityFieldType } from '../../../metadata/entity/decorators/utilities/get.entity.field.type';
import { EntityAdvancedFilterOperator } from './enums';

export enum EntityAdvancedFilterLogicalOperator {
  And = 'and',
  Or = 'or',
}

export enum EntityAdvancedFilterTargetStrategy {
  Field = 1,
  Attribute = 2,
  Search = 3,
}

export enum EntityAdvancedFilterPrincipalScopeType {
  Employee = 'employee',
  Contractor = 'contractor',
}

export interface EntityAdvancedFilterPrincipalScope {
  type: EntityAdvancedFilterPrincipalScopeType;
  placeholders: string[];
}

export interface EntityAdvancedFilterOperatorOption {
  label: string;
  value: EntityAdvancedFilterOperator;
}

export const ENTITY_FILTER_TEXT_OPERATORS: EntityAdvancedFilterOperatorOption[] = [
  { label: 'is', value: EntityAdvancedFilterOperator.Equals },
  { label: 'is not', value: EntityAdvancedFilterOperator.NotEquals },
  { label: 'contains', value: EntityAdvancedFilterOperator.Contains },
  { label: 'does not contain', value: EntityAdvancedFilterOperator.NotContains },
  { label: 'starts with', value: EntityAdvancedFilterOperator.StartsWith },
  { label: 'ends with', value: EntityAdvancedFilterOperator.EndsWith },
  { label: 'is empty', value: EntityAdvancedFilterOperator.IsEmpty },
  { label: 'is not empty', value: EntityAdvancedFilterOperator.IsNotEmpty },
];

export const ENTITY_FILTER_NUMBER_OPERATORS: EntityAdvancedFilterOperatorOption[] = [
  { label: '=', value: EntityAdvancedFilterOperator.Equals },
  { label: '!=', value: EntityAdvancedFilterOperator.NotEquals },
  { label: '>', value: EntityAdvancedFilterOperator.GreaterThan },
  { label: '>=', value: EntityAdvancedFilterOperator.GreaterThanOrEqual },
  { label: '<', value: EntityAdvancedFilterOperator.LessThan },
  { label: '<=', value: EntityAdvancedFilterOperator.LessThanOrEqual },
  { label: 'between', value: EntityAdvancedFilterOperator.Between },
  { label: 'is empty', value: EntityAdvancedFilterOperator.IsEmpty },
  { label: 'is not empty', value: EntityAdvancedFilterOperator.IsNotEmpty },
];

export const ENTITY_FILTER_DATE_OPERATORS: EntityAdvancedFilterOperatorOption[] = [
  { label: 'relative', value: EntityAdvancedFilterOperator.RelativeDate },
  { label: 'on', value: EntityAdvancedFilterOperator.On },
  { label: 'before', value: EntityAdvancedFilterOperator.Before },
  { label: 'after', value: EntityAdvancedFilterOperator.After },
  { label: 'on or before', value: EntityAdvancedFilterOperator.OnOrBefore },
  { label: 'on or after', value: EntityAdvancedFilterOperator.OnOrAfter },
  { label: 'between', value: EntityAdvancedFilterOperator.Between },
  { label: 'is empty', value: EntityAdvancedFilterOperator.IsEmpty },
  { label: 'is not empty', value: EntityAdvancedFilterOperator.IsNotEmpty },
];

export const ENTITY_FILTER_LIST_OPERATORS: EntityAdvancedFilterOperatorOption[] = [
  { label: 'is', value: EntityAdvancedFilterOperator.Equals },
  { label: 'is not', value: EntityAdvancedFilterOperator.NotEquals },
  { label: 'is any of', value: EntityAdvancedFilterOperator.In },
  { label: 'is none of', value: EntityAdvancedFilterOperator.NotIn },
  { label: 'is empty', value: EntityAdvancedFilterOperator.IsEmpty },
  { label: 'is not empty', value: EntityAdvancedFilterOperator.IsNotEmpty },
];

export function getEntityFilterOperators(field: MetadataEntityFieldData | null): EntityAdvancedFilterOperatorOption[] {
  if (!field) return ENTITY_FILTER_TEXT_OPERATORS;

  const fieldType = getMetadataEntityFieldType(field);

  switch (fieldType) {
    case MetadataEntityFieldType.Number:
    case MetadataEntityFieldType.Currency:
    case MetadataEntityFieldType.Percentage:
      return ENTITY_FILTER_NUMBER_OPERATORS;

    case MetadataEntityFieldType.Date:
    case MetadataEntityFieldType.DateTime:
    case MetadataEntityFieldType.Time:
      return ENTITY_FILTER_DATE_OPERATORS;

    case MetadataEntityFieldType.Boolean:
    case MetadataEntityFieldType.Flag:
    case MetadataEntityFieldType.Enum:
    case MetadataEntityFieldType.Lookup:
    case MetadataEntityFieldType.Parent:
    case MetadataEntityFieldType.Tag:
    case MetadataEntityFieldType.EmbeddedEntity:
      return ENTITY_FILTER_LIST_OPERATORS;

    default:
      return ENTITY_FILTER_TEXT_OPERATORS;
  }
}

export function getEntityFilterOperatorLabel(operator: EntityAdvancedFilterOperator): string | null {
  const allOperators = [...ENTITY_FILTER_TEXT_OPERATORS, ...ENTITY_FILTER_NUMBER_OPERATORS, ...ENTITY_FILTER_DATE_OPERATORS, ...ENTITY_FILTER_LIST_OPERATORS];
  return allOperators.find(o => o.value === operator)?.label ?? null;
}

export interface EntityAdvancedFilterRange<T = unknown> {
  from?: T;
  to?: T;
}

export type EntityAdvancedFilterScalarValue = string | number | boolean | Date | DateTime | Entity | null;
export type EntityAdvancedFilterDateValue = Date | DateTime | string;
export type EntityAdvancedFilterListValue = EntityAdvancedFilterScalarValue[];
export type EntityAdvancedFilterValue = EntityAdvancedFilterScalarValue | EntityAdvancedFilterListValue | FiltersNumberMethod | FiltersRelativeDateMethod | EntityAdvancedFilterRange | Record<string, unknown> | null;

export type EntityAdvancedFilterTextOperator = EntityAdvancedFilterOperator.Equals | EntityAdvancedFilterOperator.NotEquals | EntityAdvancedFilterOperator.In | EntityAdvancedFilterOperator.NotIn | EntityAdvancedFilterOperator.Contains | EntityAdvancedFilterOperator.NotContains | EntityAdvancedFilterOperator.StartsWith | EntityAdvancedFilterOperator.EndsWith | EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty;
export type EntityAdvancedFilterNumberOperator = EntityAdvancedFilterOperator.Equals | EntityAdvancedFilterOperator.NotEquals | EntityAdvancedFilterOperator.GreaterThan | EntityAdvancedFilterOperator.GreaterThanOrEqual | EntityAdvancedFilterOperator.LessThan | EntityAdvancedFilterOperator.LessThanOrEqual | EntityAdvancedFilterOperator.Between | EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty;
export type EntityAdvancedFilterBooleanOperator = EntityAdvancedFilterOperator.Equals | EntityAdvancedFilterOperator.NotEquals | EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty;
export type EntityAdvancedFilterDateOperator = EntityAdvancedFilterOperator.On | EntityAdvancedFilterOperator.Before | EntityAdvancedFilterOperator.After | EntityAdvancedFilterOperator.OnOrBefore | EntityAdvancedFilterOperator.OnOrAfter | EntityAdvancedFilterOperator.Between | EntityAdvancedFilterOperator.RelativeDate | EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty;
export type EntityAdvancedFilterReferenceOperator = EntityAdvancedFilterOperator.Equals | EntityAdvancedFilterOperator.NotEquals | EntityAdvancedFilterOperator.In | EntityAdvancedFilterOperator.NotIn | EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty;

type UnwrapFieldValue<T> = T extends Array<infer Item> ? NonNullable<Item> : NonNullable<T>;
type IsDateField<T> = UnwrapFieldValue<T> extends Date | DateTime ? true : false;
type IsNumberField<T> = UnwrapFieldValue<T> extends number ? true : false;
type IsBooleanField<T> = UnwrapFieldValue<T> extends boolean ? true : false;
type IsTextField<T> = UnwrapFieldValue<T> extends string ? true : false;
type IsEntityField<T> = UnwrapFieldValue<T> extends Entity ? true : false;

export type EntityAdvancedFilterOperatorForValue<T> = IsDateField<T> extends true
  ? EntityAdvancedFilterDateOperator
  : IsNumberField<T> extends true
    ? EntityAdvancedFilterNumberOperator
    : IsBooleanField<T> extends true
      ? EntityAdvancedFilterBooleanOperator
      : IsTextField<T> extends true
        ? EntityAdvancedFilterTextOperator
        : IsEntityField<T> extends true
          ? EntityAdvancedFilterReferenceOperator
          : EntityAdvancedFilterReferenceOperator;

export type EntityAdvancedFilterValueForOperator<T, TOperator extends EntityAdvancedFilterOperator> = TOperator extends EntityAdvancedFilterOperator.IsEmpty | EntityAdvancedFilterOperator.IsNotEmpty
  ? never
  : TOperator extends EntityAdvancedFilterOperator.In | EntityAdvancedFilterOperator.NotIn
    ? UnwrapFieldValue<T>[]
    : TOperator extends EntityAdvancedFilterOperator.Contains | EntityAdvancedFilterOperator.NotContains | EntityAdvancedFilterOperator.StartsWith | EntityAdvancedFilterOperator.EndsWith
      ? string
      : TOperator extends EntityAdvancedFilterOperator.Between
        ? IsDateField<T> extends true
          ? EntityAdvancedFilterRange<EntityAdvancedFilterDateValue>
          : EntityAdvancedFilterRange<UnwrapFieldValue<T>>
        : TOperator extends EntityAdvancedFilterOperator.RelativeDate
          ? FiltersRelativeDateMethod
          : TOperator extends EntityAdvancedFilterOperator.On | EntityAdvancedFilterOperator.Before | EntityAdvancedFilterOperator.After | EntityAdvancedFilterOperator.OnOrBefore | EntityAdvancedFilterOperator.OnOrAfter
            ? EntityAdvancedFilterDateValue
            : UnwrapFieldValue<T>;

type EntityAdvancedFilterConditionBase<T, K extends FlattenType<T>, TOperator extends EntityAdvancedFilterOperator> = {
  fieldId: K;
  operator: TOperator;
} & (EntityAdvancedFilterValueForOperator<PathValue<T, K>, TOperator> extends never
  ? { value?: never }
  : { value?: EntityAdvancedFilterValueForOperator<PathValue<T, K>, TOperator> });

export type EntityAdvancedFilterCondition<T = any, K extends FlattenType<T> = FlattenType<T>> = K extends FlattenType<T>
  ? EntityAdvancedFilterConditionBase<T, K, EntityAdvancedFilterOperatorForValue<PathValue<T, K>>>
  : never;

export interface EntityAdvancedFilterGroup<T = any> {
  operator: EntityAdvancedFilterLogicalOperator | 'and' | 'or';
  rules: EntityAdvancedFilterNode<T>[];
  principalScope?: EntityAdvancedFilterPrincipalScope;
}

export interface EntityAdvancedFilterConditionLoose {
  fieldId: string;
  operator: EntityAdvancedFilterOperator;
  value?: EntityAdvancedFilterValue;
}

export type EntityAdvancedFilterNode<T = any> = EntityAdvancedFilterCondition<T> | EntityAdvancedFilterGroup<T>;
export type EntityAdvancedFilterValueForField<T, K extends FlattenType<T>> = EntityAdvancedFilterValueForOperator<PathValue<T, K>, EntityAdvancedFilterOperatorForValue<PathValue<T, K>>>;
export type EntityAdvancedFilterOperatorForField<T, K extends FlattenType<T>> = EntityAdvancedFilterOperatorForValue<PathValue<T, K>>;
